import { Typography } from '@material-ui/core'
import { SuspendedDriverReasons } from 'shared/models/userTimeline'

import { DriverStatus, TEAM_OFFBOARDING_STATUSES } from '../../../helpers/constants'
import { UnapproveTeamReasons } from './TeamFSMTransitions'

export const DriverTransitions = {
  makeEligible: {
    transitionString: 'Eligible',
    transitionAction: 'mark_eligible',
    confirmationMessage: () => {
      return (
        <Typography>
          The status ‘Eligible’ is for a driver applicant who has cleared all compliance requirements
          including background check and drug test.
          <br />
          Once you change the status to ‘Eligible’, the driver will:
          <br />
          &#8226; be able to proceed to their next step of driver application.
          <br />
          &#8226; sign the agreement and contract sent by the Sales team, if the applicant is the fleet owner.
        </Typography>
      )
    },
    transitionTitle: () => {
      return 'Changing the driver status to “Eligible”.'
    },
  },
  suspendDriver: {
    transitionString: 'Suspend',
    transitionAction: 'suspend',
    confirmationMessage: () => {
      return (
        <Typography>
          The status ‘Suspended Temporarily’ is for a driver who should not be booking a load for the short
          term.
          <br />
          Please remember to change it back to ‘Active’ when they are ready to come back or ‘Deactivate’ if
          the driver is officially deactivated.
          <br />
          Once you change the status to ‘Suspended Temporarily’, the driver will:
          <br />
          &#8226; not be able to book loads <br />
          &#8226; still have access to the app
          <br />
        </Typography>
      )
    },
    transitionTitle: () => {
      return 'Changing the driver status to “Suspended Temporarily”.'
    },
  },
  abortDriver: {
    transitionString: 'Abort Process',
    transitionAction: 'abort_process',
    confirmationMessage: () => {
      return (
        <Typography>
          The status ‘Process Aborted’ is for a driver applicant who voluntarily aborted their application
          process.
          <br />
          The applicant would have to go through the driver application again if they want to reapply.
          <br />
        </Typography>
      )
    },
    transitionTitle: () => {
      return 'Changing the driver status to “Process Aborted”.'
    },
  },
  onboardDriver: {
    transitionString: 'Onboard',
    transitionAction: 'onboard',
    confirmationMessage: () => {
      return (
        <Typography>
          <h1>Change Status to Onboarding</h1>
          Change the driver’s status to Onboarding if their fleet’s business owner has signed the lease
          agreement and legal contract, and they’re preparing to start driving with CloudTrucks. <br />
          <br />
          Once the status is changed to Onboarding, the driver will:
          <br />
          <br />
          &#8226; Receive an email to complete road preparation tasks
          <br />
          &#8226; Be able to familiarize themselves with the app once their fleet has been approved
          <br />
          <br />
          The driver won’t be able to view the load board in the app until the fleet is approved.
        </Typography>
      )
    },
    transitionTitle: () => {
      'Changing the driver status to “Onboarding”.'
    },
  },
  deactivateDriver: {
    transitionString: 'Deactivate',
    transitionAction: 'deactivate',
    confirmationMessage: () => {
      return (
        <Typography>
          The status ‘Deactivated’ is for a driver who voluntarily churned or was officially deactivated by
          Cloudtrucks.
          <br />
          <br />
          Once you change the status to ‘Deactivated’, the driver will <br />
          &#8226; not be able to access the App
          <br />
          &#8226; have to go through the driver application again if they want to come back to CloudTrucks
          <br />
        </Typography>
      )
    },
    transitionTitle: () => {
      return 'Changing the driver status to “Deactivated”.'
    },
  },
  unsuspendDriver: {
    transitionString: 'Unsuspend',
    transitionAction: 'unsuspend',
    confirmationMessage: () => {
      return (
        <Typography>
          When you unsuspend the driver, their status changes back to ‘Active’.
          <br />
          Once you change the status to ‘Active’, the driver will:
          <br />
          &#8226; book loads.
          <br />
          &#8226; access the App..
        </Typography>
      )
    },
    transitionTitle: () => {
      return 'Changing back the driver status to “Active”.'
    },
  },
  activateDriver: {
    transitionString: 'Activate',
    transitionAction: 'approve',
    confirmationMessage: () => {
      return (
        <Typography>
          The status to ‘Active’ is for a driver who has completed all required onboarding tasks including the
          safety call with the compliance team.
          <br />
          ‘Active’ drivers are ready to book a load and start driving with CloudTrucks.
          <br />
          Once you change the status to ‘Active’, the driver will:
          <br />
          &#8226; be able to book loads in App.
          <br />
          &#8226; receive a celebratory email to encourage them to book a first job.
        </Typography>
      )
    },
    transitionTitle: () => {
      return 'Changing the driver status to “Active”.'
    },
  },
  rejectDriver: {
    transitionString: 'Reject',
    transitionAction: 'reject',
    confirmationMessage: () => {
      return (
        <Typography>
          The status ‘Rejected’ is for a driver applicant who didn’t meet all compliance requirements and
          qualification at CloudTrucks.
          <br />
          The applicant would have to go through the driver application again if they want to reapply.
          <br />
        </Typography>
      )
    },
    transitionTitle: () => {
      return 'Changing the driver status to “Rejected”.'
    },
  },
  returnToApplying: {
    transitionString: 'Applying',
    transitionAction: 'return_to_applying',
    confirmationMessage: () => {
      return <Typography>Return the driver to the application process</Typography>
    },
  },
  returnToOnboarding: {
    transitionString: 'Onboarding',
    transitionAction: 'return_to_onboarding',
    confirmationMessage: () => {
      return (
        <Typography>
          Return the driver to the onboarding process
          <strong>
            If it has been over 90 days since the driver was deactivated, they need to go back to the applying
            status, not onboarding
          </strong>
        </Typography>
      )
    },
  },
}

export const DriverFSMTransitions = {
  [DriverStatus.Applying]: [
    DriverTransitions.makeEligible,
    DriverTransitions.rejectDriver,
    DriverTransitions.abortDriver,
  ],
  [DriverStatus.Eligible]: [
    DriverTransitions.onboardDriver,
    DriverTransitions.abortDriver,
    DriverTransitions.rejectDriver,
  ],
  [DriverStatus.Onboarding]: [
    DriverTransitions.activateDriver,
    DriverTransitions.abortDriver,
    DriverTransitions.rejectDriver,
  ],
  [DriverStatus.Suspended]: [DriverTransitions.unsuspendDriver, DriverTransitions.deactivateDriver],
  [DriverStatus.Active]: [DriverTransitions.suspendDriver, DriverTransitions.deactivateDriver],
  [DriverStatus.Aborted]: [DriverTransitions.returnToApplying, DriverTransitions.returnToOnboarding],
  [DriverStatus.Rejected]: [DriverTransitions.returnToApplying],
  [DriverStatus.Deactivated]: [DriverTransitions.returnToApplying, DriverTransitions.returnToOnboarding],
}

export const DriverDeactivateReasons: Record<string, any> = {
  LEFT_FLEET: { id: 'LEFT_FLEET', name: 'Driver (non-admin) left the fleet' },
  ...UnapproveTeamReasons,
}

export { SuspendedDriverReasons }

export const getDriverTransitionProps = (user: any) => {
  if (!user) return null

  const status = user?.default_team_membership?.driver_status?.replace(/\s+/g, '_').toUpperCase()
  if (
    user.team?.status &&
    status &&
    (Object.values(TEAM_OFFBOARDING_STATUSES).includes(user?.team?.status) ||
      !Object.keys(DriverFSMTransitions).includes(status))
  ) {
    return null
  }
  return DriverFSMTransitions[status]
}
